/**
 * F2. Собеседование: при `work.first` и `work.change` три реплики
 * работодателя, на каждую три ответа — честный / уверенный / скромный.
 * Что ценится, зависит от уровня места (`interviewTier`): на низком —
 * скромность, на среднем — честность, на высоком — уверенность. Подсказка
 * (`hint` = верный индекс) видна, когда среднее знаний и отношений ≥ 60.
 *
 * Расчётный уровень — `jobLevelFor` плюс бонус за права (как `hireLevel`
 * в решении); итог: три верных ответа — +1, не больше одного верного — −1,
 * иначе расчётный; всегда в пределах 0..3 и доступных вариантов решения.
 * Для `work.first` исход — `level<n>`; для `work.change` — `change` с
 * `flags.interviewLevel` = итог, если он выше текущего уровня (решение
 * берёт уровень из флага, а не из жребия), иначе `stay`. Запись журнала
 * всегда называет реальный уровень: при `stay` — нынешний.
 * «Пропустить» — расчётный уровень без поправки (для смены — автовыбор).
 */
import type { Dataset, GamblingLaw } from '@life/data';
import {
  FLAG_INTERVIEW_LEVEL,
  FLAG_LICENSE,
  jobLevelFor,
  LICENSE_JOB_LEVEL_BONUS,
  MAX_JOB_LEVEL,
  type Life,
  type MinigameOutcome,
  type Rng,
} from '@life/engine';
import type { MinigameDef } from '../types';
import { nothing, pendingOf } from './common';

export const INTERVIEW_LINES = 3;
export const INTERVIEW_ANSWERS = ['honest', 'confident', 'modest'] as const;
export type InterviewAnswer = 0 | 1 | 2;
/** Среднее знаний и отношений, с которого подсвечивается верный ответ. */
export const INTERVIEW_HINT_THRESHOLD = 60;
/** Верный ответ по ярусу места: низкий — скромность, средний — честность, высокий — уверенность. */
export const INTERVIEW_CORRECT_BY_TIER: readonly InterviewAnswer[] = [2, 0, 1];

/** Ярус реплик по уровню работы: 0 → низкий, 1 → средний, 2–3 → высокий. */
export function interviewTier(level: number): number {
  if (level <= 0) return 0;
  return level === 1 ? 1 : 2;
}

export function interviewLineKey(tier: number, line: number): string {
  return `minigame.interview.${tier}.${line}`;
}

export function interviewAnswerKey(tier: number, line: number, answer: number): string {
  return `${interviewLineKey(tier, line)}.${INTERVIEW_ANSWERS[answer]}`;
}

export interface InterviewLine {
  key: string;
  /** Ключи трёх ответов в порядке INTERVIEW_ANSWERS. */
  answers: string[];
  correct: InterviewAnswer;
  /** Подсвеченный ответ или null. */
  hint: InterviewAnswer | null;
}

export interface InterviewState {
  kind: 'work.first' | 'work.change';
  /** Расчётный уровень 0..3. */
  level: number;
  /** Верхняя граница доступных уровней решения (для смены работы — 3). */
  maxLevel: number;
  /** Текущий уровень при смене работы, иначе null. */
  currentLevel: number | null;
  tier: number;
  lines: InterviewLine[];
  timeoutOption: string;
}

export type InterviewInput = { picks: InterviewAnswer[] };

const INTERVIEW_KINDS = ['work.first', 'work.change'] as const;

function levelOfOption(id: string): number | null {
  if (!id.startsWith('level')) return null;
  const n = Number(id.slice('level'.length));
  return Number.isInteger(n) ? n : null;
}

/** Уровень, который реально получится при таком исходе: для смены работы ниже нынешнего не опускаемся. */
function finalLevel(state: InterviewState, level: number): number {
  const clamped = Math.max(0, Math.min(state.maxLevel, level));
  return state.kind === 'work.change' && state.currentLevel !== null ? Math.max(state.currentLevel, clamped) : clamped;
}

function changes(state: InterviewState, level: number): boolean {
  return state.kind === 'work.change' && state.currentLevel !== null && level > state.currentLevel;
}

function resultOption(state: InterviewState, level: number): string {
  if (state.kind === 'work.change') return changes(state, level) ? 'change' : 'stay';
  return `level${finalLevel(state, level)}`;
}

export const interview: MinigameDef<InterviewInput, InterviewState> = {
  id: 'interview',
  section: 'F',
  triggers: [
    { kind: 'decision', decision: 'work.first' },
    { kind: 'decision', decision: 'work.change' },
  ],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return pendingOf(life, INTERVIEW_KINDS) !== null;
  },
  setup(life: Life, _ds: Dataset, rng: Rng): InterviewState {
    const decision = pendingOf(life, INTERVIEW_KINDS);
    const kind = (decision?.kind ?? 'work.first') as InterviewState['kind'];
    let maxLevel: number = MAX_JOB_LEVEL;
    if (kind === 'work.first' && decision) {
      const levels = decision.options.filter((o) => o.available).map((o) => levelOfOption(o.id)).filter((n): n is number => n !== null);
      maxLevel = levels.length > 0 ? Math.max(...levels) : 0;
    }
    const bonus = life.flags[FLAG_LICENSE] === true ? LICENSE_JOB_LEVEL_BONUS : 0;
    const drawn = Math.min(MAX_JOB_LEVEL, jobLevelFor(life.education, life.stats.knowledge, rng) + bonus);
    const level = Math.min(drawn, maxLevel);
    const tier = interviewTier(level);
    const correct = INTERVIEW_CORRECT_BY_TIER[tier] as InterviewAnswer;
    const hinted = (life.stats.knowledge + life.stats.relations) / 2 >= INTERVIEW_HINT_THRESHOLD;
    const lines: InterviewLine[] = [];
    for (let i = 0; i < INTERVIEW_LINES; i += 1) {
      lines.push({
        key: interviewLineKey(tier, i),
        answers: INTERVIEW_ANSWERS.map((_a, j) => interviewAnswerKey(tier, i, j)),
        correct,
        hint: hinted ? correct : null,
      });
    }
    return {
      kind,
      level,
      maxLevel,
      currentLevel: kind === 'work.change' ? (life.job?.level ?? null) : null,
      tier,
      lines,
      timeoutOption: decision?.timeoutOption ?? '',
    };
  },
  play(state: InterviewState, input: InterviewInput, _life: Life): MinigameOutcome {
    const picks = Array.isArray(input.picks) ? input.picks : [];
    let right = 0;
    state.lines.forEach((line, i) => {
      if (picks[i] === line.correct) right += 1;
    });
    const shift = right >= INTERVIEW_LINES ? 1 : right <= 1 ? -1 : 0;
    const level = Math.max(0, Math.min(MAX_JOB_LEVEL, state.level + shift));
    const option = resultOption(state, level);
    const real = finalLevel(state, level);
    if (state.kind === 'work.change' && !changes(state, level)) {
      return { ...nothing(), decisionOption: option, logKey: 'minigame.interview.stay', logParams: { level: real } };
    }
    const outcome: MinigameOutcome = {
      ...nothing(),
      decisionOption: option,
      logKey: shift > 0 ? 'minigame.interview.up' : shift < 0 ? 'minigame.interview.down' : 'minigame.interview.same',
      logParams: { level: real },
    };
    if (state.kind === 'work.change') outcome.flags = { [FLAG_INTERVIEW_LEVEL]: real };
    return outcome;
  },
  expected(state: InterviewState): MinigameOutcome {
    if (state.kind === 'work.change') return { ...nothing(), decisionOption: state.timeoutOption || 'stay' };
    return { ...nothing(), decisionOption: resultOption(state, state.level) };
  },
};
